import { useId } from 'react';

import Badge from 'components/partials/badge/badge';
import Button from 'components/partials/button/button';
import Drawer from 'components/partials/drawer/drawer';
import Input from 'components/partials/input/input';
import Textarea from 'components/partials/textarea/textarea';

import DimensionBlock from 'containers/admin/clients/client/tabs-components/critical-populations/components/dimension-block';
import OriginBadge from 'containers/admin/clients/client/tabs-components/critical-populations/components/origin-badge';
import {
  CREATE_NOTE_HELPER,
  EDIT_STATUS_BADGE,
  MAX_DIMENSIONS,
  NOTE_MAX_LENGTH,
  NOTE_TEXTAREA_ROWS,
  NOTE_TEXTAREA_STYLES,
  TITLE_PREVIEW_PLACEHOLDER,
} from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.constants';
import type { FormDrawerMode } from 'containers/admin/clients/client/tabs-components/critical-populations/critical-populations.types';
import { useCaptureForm } from 'containers/admin/clients/client/tabs-components/critical-populations/hooks/use-capture-form';

import type { CriticalPopulationResponse } from 'api/generated/schemas';

import Icons from 'assets/icons';

interface FormDrawerProps {
  open: boolean;
  onExited: () => void;
  mode: FormDrawerMode;
  row?: CriticalPopulationResponse;
  clientId: number;
  enteringClassYear: number;
  onClose: () => void;
}

const FormDrawer = ({
  open,
  onExited,
  mode,
  row,
  clientId,
  enteringClassYear,
  onClose,
}: FormDrawerProps) => {
  const noteId = useId();
  const titleId = useId();
  const form = useCaptureForm({
    clientId,
    mode,
    row,
    enteringClassYear,
    onClose,
  });
  const isCreate = mode === 'create';
  const statusBadge = row?.status ? EDIT_STATUS_BADGE[row.status] : undefined;

  return (
    <Drawer
      open={open}
      onExited={onExited}
      onClose={onClose}
      title={
        isCreate
          ? 'Add custom Critical Element'
          : row?.summary ?? 'Edit Critical Element'
      }
      headerSlot={
        <div className="flex gap-2">
          {isCreate ? (
            <Badge text="Custom" variant="reserve-outlined" />
          ) : (
            <>
              {statusBadge && (
                <Badge
                  text={statusBadge.text}
                  variant={statusBadge.variant}
                  showStatusDot
                />
              )}
              <OriginBadge origin={row?.origin} />
            </>
          )}
        </div>
      }
      footer={
        <>
          <Button
            variant="brand-filled"
            size="md"
            disabled={form.isSubmitDisabled}
            loading={form.isPending}
            onClick={form.handleSubmit}
            data-testid="form-drawer-submit"
          >
            {isCreate ? 'Promote' : 'Save changes'}
          </Button>
          <Button
            variant="neutral-outlined"
            size="md"
            onClick={onClose}
            data-testid="form-drawer-cancel"
          >
            Cancel
          </Button>
        </>
      }
      data-testid="form-drawer"
    >
      <div className="flex flex-col gap-1">
        <Input
          label="Critical Element name"
          labelFor={titleId}
          labelInTop
          required
          placeholder={TITLE_PREVIEW_PLACEHOLDER}
          value={form.titleValue}
          onChange={(event) => form.handleTitleChange(event.target.value)}
          errorsNames={
            form.previewError || form.titleError
              ? { message: form.previewError ?? form.titleError }
              : undefined
          }
          data-testid="form-drawer-title"
        />
        {form.canRestoreTitle && (
          <div className="flex">
            <Button
              variant="reserve-text-link"
              size="sm"
              onClick={form.handleRestoreTitle}
              data-testid="form-drawer-restore-title"
            >
              Use generated name
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-px">
          <h3 className="text-base font-bold text-gray-700">Dimensions</h3>
          <p className="text-sm text-gray-700">
            Select up to {MAX_DIMENSIONS} dimensions. Each dimension can be used
            once.
          </p>
        </div>

        {form.isCatalogLoading && (
          <p
            className="text-sm text-gray-500"
            data-testid="form-drawer-loading"
          >
            Loading dimensions...
          </p>
        )}

        {form.isCatalogError && (
          <div role="alert" className="flex flex-col items-start gap-2">
            <p className="text-sm text-gray-700">
              Couldn&apos;t load dimensions.
            </p>
            <Button
              variant="neutral-outlined"
              size="sm"
              onClick={form.retryCatalog}
            >
              Retry
            </Button>
          </div>
        )}

        {form.blocks.map((block, index) => (
          <DimensionBlock
            key={block.key}
            block={block}
            index={index}
            canRemove={form.canRemoveDimension}
            onAttributeChange={form.handleAttributeChange}
            onValuesChange={form.handleValuesChange}
            onRemove={form.handleRemoveDimension}
          />
        ))}

        {form.canAddDimension && (
          <div className="flex">
            <Button
              variant="reserve-outlined"
              size="sm"
              iconBefore={<Icons.Plus aria-hidden="true" />}
              onClick={form.handleAddDimension}
              data-testid="form-drawer-add-dimension"
            >
              Add dimension
            </Button>
          </div>
        )}
      </div>

      {isCreate && (
        <Textarea
          label="Reason"
          labelFor={noteId}
          required
          helperText={CREATE_NOTE_HELPER}
          helperTextPosition="below"
          maxLength={NOTE_MAX_LENGTH}
          rows={NOTE_TEXTAREA_ROWS}
          textareaStyles={NOTE_TEXTAREA_STYLES}
          value={form.noteValue}
          onChange={form.handleNoteChange}
          errorsNames={form.noteError ? { message: form.noteError } : undefined}
          data-testid="form-drawer-note"
        />
      )}
    </Drawer>
  );
};

export default FormDrawer;
